import * as React from 'preact/compat'
import Loading from '../Loading'
import Api from '../../api'
import GameDateEditor from './GameDateEditor'
import { captureFetchException, loadStylesheet, sgVersion, showModal } from '../../common/helpers'
import GamePlatformsEditor, { type Platform } from './GamePlatformsEditor'
import GameCompaniesEditor from './GameCompaniesEditor'
import GameFixSuggestionLine from './GameFixSuggestionLine'
import GameTitlesEditor from './GameTitlesEditor'
import GameCoversEditor from './GameCoversEditor'
import formCss from '../../../css/form.pcss.json'
import buttonsCss from '../../../css/buttons.pcss.json'
import commonCss from '../../../css/common.pcss.json'
import { NotificationPermissionRequestType, promptNotificationsPermission } from '../../common/cloud-messaging'
import cn from 'classnames'
import SVG from '../SVG'
import { showToast } from '../toast'
import Modal from '../Modal'
import GameTagsEditor from "./GameTagsEditor";

export enum GameEditorMode {
  suggest,
  edit,
  suggest_game
}

interface PropTypes {
  game_id: number | string | null,
  mode: GameEditorMode,
  subgrid?: boolean,
  onSuccess?: (requestInfo: {
    isRequest: boolean,
    id?: number,
    name: string,
    poster?: string
  }) => void,
  onLoad?: () => void,
  hidePushPrompt?: boolean,
  modal?: React.RefObject<Modal>,
  requestId?: number
}

export interface GameEditorState {
  loaded: boolean,
  errors: {
    [key: string]: boolean
  },
  gameInfo?: {
    gameId?: number,
    titles: Array<{
      TitleId: number,
      GameName: string,
      GameKeywords: string,
      GameNameUrl: string,
      GameNamePrimary: boolean,
      remove_from_search: boolean
    }>,
    GameSite: string,
    GameRusSite: string,
    GameDate: string,
    GamePlatform: Array<Platform>,
    GameJanr: string[],
    GameDevelopers: Array<{
      id: number,
      name: string
    }>,
    GamePublishers: Array<{
      id: number,
      name: string
    }>,
    covers: {
      square_poster: string,
      large_poster: string,
    },
    SteamId?: string,
    OldLogo?: string,
    GameReq1?: string,
    GameReq2?: string,
    steam_id?: string,
    GameInfo?: string,
    notes?: string
  }
  suggestions?: {
    [key: string]: Array<{
      user: {
        url: string,
        name: string,
        avatar: string,
        inTeam: boolean
      },
      id: number,
      value: string,
      isOwner: boolean
    }>
  },
  isEditor?: boolean,
  initialGameName?: string,
  gameNameError?: string
}

export default class GameEditor extends React.Component<PropTypes, GameEditorState> {

  state: GameEditorState = {
    loaded: false,
    errors: {}
  }

  componentWillMount() {
    // noinspection JSIgnoredPromiseFromCall
    loadStylesheet('form')
    // noinspection JSIgnoredPromiseFromCall
    loadStylesheet('game-editor');
  }

  componentDidMount() {
    const { game_id } = this.props

    if (game_id === 'new') {
      this.setState({
        loaded: true,
        gameInfo: {
          titles: [],
          GameSite: '',
          GameRusSite: '',
          GameDate: '0000-00-00',
          GamePlatform: [],
          GameJanr: [],
          GameDevelopers: [],
          GamePublishers: [],
          covers: {
            square_poster: '',
            large_poster: '',
          },
          steam_id: null
        },
        isEditor: true,
        initialGameName: '',
      })
      return
    }

    if (game_id.toString().startsWith('request-')) {
      const requestId = game_id.toString().replace('request-', '')
      Api.get({ url: `game/get-add-request/${requestId}` })
        .then((response) => {
          this.setState({
            loaded: true,
            gameInfo: response.gameInfo as GameEditorState['gameInfo'],
            suggestions: response.suggestions as GameEditorState['suggestions'],
            isEditor: response.isEditor as GameEditorState['isEditor'],
            initialGameName: (response.gameInfo as GameEditorState['gameInfo'])?.titles.find(t => t.GameNamePrimary)?.GameName ?? ''
          })
        })
        .catch(async (err) => {
          captureFetchException(err)
          showToast({
            type: 'error',
            text: 'Не удалось загрузить данные об игре. Попробуй позже.',
          })
          if (this.props.modal) {
            await this.props.modal.current?.close()
          }
        })
      return
    }

    Api.get({ url: `game/info/${game_id}` })
      .then(response => {
        this.setState({
          loaded: true,
          gameInfo: response.gameInfo as GameEditorState['gameInfo'],
          suggestions: response.suggestions as GameEditorState['suggestions'],
          isEditor: response.isEditor as GameEditorState['isEditor'],
          initialGameName: (response.gameInfo as GameEditorState['gameInfo'])?.titles.find(t => t.GameNamePrimary)?.GameName ?? ''
        })
      })
      .catch(async (err) => {
        captureFetchException(err)
        showToast({
          type: 'error',
          text: 'Не удалось загрузить данные об игре. Попробуй позже.',
        })
        if (this.props.modal) {
          await this.props.modal.current?.close()
        }
      })
  }

  componentDidUpdate(_prevProps: PropTypes, prevState: GameEditorState) {
    if (this.props.onLoad && prevState.gameInfo !== this.state.gameInfo) {
      this.props.onLoad()
    }
  }

  acceptSuggestion = (id: number, altField: string, field: string, value: string) => {
    const suggestions = { ...this.state.suggestions }
    const gameInfo = { ...this.state.gameInfo }
    suggestions[altField] = suggestions[altField].filter(s => s.id !== id)
    if (['square_poster', 'large_poster'].includes(field)) {
      gameInfo.covers = { ...gameInfo.covers }
      gameInfo.covers[field] = value
    } else {
      gameInfo[altField] = value
    }
    this.setState({
      suggestions,
      gameInfo
    })
  }

  rejectSuggestion = (id: number, altField: string) => {
    const suggestions = { ...this.state.suggestions }
    suggestions[altField] = suggestions[altField].filter(s => s.id !== id)
    this.setState({
      suggestions
    })
  }

  renderSuggestions(field: string, altField?: string) {
    const { suggestions, isEditor } = this.state
    if (!suggestions) {
      return null
    }
    if (!altField) {
      altField = field
    }
    const suggestionsList = suggestions[altField]
    if (!suggestionsList) {
      return null
    }
    return <div style='margin-top: 16px; display: flex; flex-direction: column; row-gap: 8px;'>
      {suggestionsList.map(suggestion => <GameFixSuggestionLine
        key={suggestion.id}
        suggestion={suggestion}
        field={field}
        altField={altField}
        onAccept={this.acceptSuggestion}
        onReject={this.rejectSuggestion}
        isEditor={isEditor}
      />)}
    </div>
  }

  onSubmit = (event: React.TargetedEvent<HTMLFormElement>) => {
    event.preventDefault()
    const form = event.currentTarget
    const formData = new FormData(form)
    let url: string, successText: string;
    switch (this.props.mode) {
      case GameEditorMode.suggest:
        url = 'game/suggest'
        successText = 'Предложения отправлены и будут рассмотрены модератором. Спасибо!'
        break
      case GameEditorMode.edit:
        url = 'game/edit'
        successText = 'Изменения сохранены.'
        break
      case GameEditorMode.suggest_game:
        url = 'game/suggest-game'
        successText = 'Предложенная игра сохранена и будет рассмотрена модератором. Спасибо!'
        break
    }

    Api.post({ url, body: formData })
      .then(async (result) => {
        showToast({
          type: 'success',
          text: successText
        })
        if (this.props.modal) {
          await this.props.modal.current?.close()
        }
        if (this.props.onSuccess) {
          this.props.onSuccess(result as {
            isRequest: boolean,
            id?: number,
            name: string,
            poster?: string,
            url?: string
          })
        }
        if (this.props.mode === GameEditorMode.edit) {
          if (result.redirect) {
            window.location = result.redirect
          } else {
            window.location.reload()
          }
        } else if (!this.props.hidePushPrompt) {
          return promptNotificationsPermission(NotificationPermissionRequestType.fix_suggestion)
        }
      })
      .catch(err => {
        captureFetchException(err)
        showToast({
          type: 'error',
          text: err.body?.message || 'Что-то сломалось. Пожалуйста, напиши нам в обратную связь.',
        })
      })
  }

  addTitle = (title: string) => {
    const gameInfo = { ...this.state.gameInfo }
    const titles = [...gameInfo.titles]
    titles.push({
      TitleId: null,
      GameName: title,
      GameKeywords: '',
      GameNameUrl: '',
      GameNamePrimary: false,
      remove_from_search: false
    })
    this.updateTitles(titles)
  }

  updateTitles = (titles: GameEditorState['gameInfo']['titles']) => {
    const gameInfo = { ...this.state.gameInfo }
    gameInfo.titles = titles
    this.setState({
      gameInfo
    })
  }

  setSquareCover = (cover: string) => {
    const gameInfo = { ...this.state.gameInfo }
    const covers = { ...gameInfo.covers }
    covers.square_poster = cover
    gameInfo.covers = covers
    this.setState({
      gameInfo
    })
  }

  setLargeCover = (cover: string) => {
    const gameInfo = { ...this.state.gameInfo }
    const covers = { ...gameInfo.covers }
    covers.large_poster = cover
    gameInfo.covers = covers
    this.setState({
      gameInfo
    })
  }

  setDate = (date: string) => {
    const gameInfo = { ...this.state.gameInfo }
    gameInfo.GameDate = date
    this.setState({
      gameInfo
    })
  }

  addPlatform = (platform: Platform) => {
    const gameInfo = { ...this.state.gameInfo }
    const platforms = [...gameInfo.GamePlatform]
    platforms.push(platform)
    gameInfo.GamePlatform = platforms
    this.setState({
      gameInfo
    })
  }

  addGenre = (genre: string) => {
    const gameInfo = { ...this.state.gameInfo }
    const genres = [...gameInfo.GameJanr]
    genres.push(genre)
    gameInfo.GameJanr = genres
    this.setState({
      gameInfo
    })
  }

  addDeveloper = (developer: { id: number, name: string }) => {
    const gameInfo = { ...this.state.gameInfo }
    const developers = [...gameInfo.GameDevelopers]
    developers.push(developer)
    gameInfo.GameDevelopers = developers
    this.setState({
      gameInfo
    })
  }

  addPublisher = (publisher: { id: number, name: string }) => {
    const gameInfo = { ...this.state.gameInfo }
    const publishers = [...gameInfo.GamePublishers]
    publishers.push(publisher)
    gameInfo.GamePublishers = publishers
    this.setState({
      gameInfo
    })
  }

  setSite = (site: string) => {
    const gameInfo = { ...this.state.gameInfo }
    gameInfo.GameSite = site
    this.setState({
      gameInfo
    })
  }

  setRusSite = (site: string) => {
    const gameInfo = { ...this.state.gameInfo }
    gameInfo.GameRusSite = site
    this.setState({
      gameInfo
    })
  }

  setSteamIdFromLink = (event: React.TargetedEvent<HTMLInputElement>) => {
    this.setSteamId(event.currentTarget.value)
  }

  setSteamId = (steamUrl: string) => {
    const gameInfo = { ...this.state.gameInfo }
    const steamId = steamUrl.match(/app\/(\\d+)/)?.[1]
    if (steamId) {
      gameInfo.steam_id = steamUrl
      gameInfo.SteamId = steamId
    } else {
      gameInfo.steam_id = null
      gameInfo.SteamId = null
    }
    this.setState({
      gameInfo
    })
  }

  getGameInfo = () => {
    return this.state.gameInfo
  }

  checkGameName = async (name: string, titleId?: number) => {
    const body: {
      id?: number,
      titleId?: number,
      name: string
    } = {
      name
    }
    if (typeof this.props.game_id === 'number') {
      body.id = this.props.game_id
    }
    if (typeof this.state.gameInfo.gameId === 'number') {
      body.id = this.state.gameInfo.gameId
    }
    if (typeof titleId === 'number') {
      body.titleId = titleId
    }
    return await Api.post<boolean>({ url: 'games/game-name-exists', body })
  }

  checkGameUrl = async (url: string, titleId?: number) => {
    const body: {
      id?: number,
      titleId?: number,
      url: string
    } = {
      url
    }
    if (typeof this.props.game_id === 'number') {
      body.id = this.props.game_id
    }
    if (typeof this.state.gameInfo.gameId === 'number') {
      body.id = this.state.gameInfo.gameId
    }
    if (typeof titleId === 'number') {
      body.titleId = titleId
    }
    return await Api.post<boolean>({ url: 'games/game-url-exists', body })
  }

  setTitle = (event: React.TargetedEvent<HTMLInputElement>) => {
    const gameInfo = { ...this.state.gameInfo }
    let title = event.currentTarget.value
    title = title.replace('`', "'")
      .replace('’', "'")
    gameInfo.titles = [
      {
        TitleId: null,
        GameName: title,
        GameKeywords: '',
        GameNameUrl: '',
        GameNamePrimary: true,
        remove_from_search: false
      }
    ]
    this.setState({
      gameInfo
    }, async () => {
      if (title !== this.state.initialGameName && await this.checkGameName(title)) {
        this.setState({
          gameNameError: 'Игра с таким названием уже существует'
        })
        this.addError('gameName')
        return
      }
      this.setState({
        gameNameError: null
      })
      this.removeError('gameName')
    })
  }

  addError = (field: string) => {
    const errors = { ...this.state.errors }
    errors[field] = true
    this.setState({
      errors
    })
  }

  removeError = (field: string) => {
    const errors = { ...this.state.errors }
    delete errors[field]
    this.setState({
      errors
    })
  }

  showCreateCompany = async () => {
    const { default: CompanyCreateModal } = await import(`/dist/js/components/CompanyCreateModal.js?${sgVersion()}`)
    showModal(CompanyCreateModal)
  }

  render() {
    const { mode, game_id, subgrid, requestId } = this.props
    const { gameInfo, loaded, suggestions, gameNameError } = this.state

    let content = <Loading/>
    const isRequest = game_id.toString().startsWith('request-')

    let addCompany = mode === GameEditorMode.edit ? <>
      <button
        type='button'
        className={cn(buttonsCss['small-edit-button'])}
        style='padding-left: var(--button-icon-padding); --button-icon-size: 18px; --button-icon-padding: 7px'
        onClick={this.showCreateCompany}
      >
        <SVG i='sg-new/plus' adaptive/>
        создать компанию
      </button>
    </> : ''

    let formTitle = ''

    switch (mode) {
      case GameEditorMode.suggest:
        formTitle = 'Предложить изменения'
        break
      case GameEditorMode.edit:
        formTitle = 'Редактирование игры'
        break
      case GameEditorMode.suggest_game:
        formTitle = 'Предложить игру'
        break
    }

    if (loaded) {
      const mainTitle = gameInfo?.titles.find(t => t.GameNamePrimary)?.GameName ?? ''
      const isValid = Object.keys(this.state.errors).length === 0

      const formStyle: React.JSX.CSSProperties = {
        maxWidth: '685px'
      }

      if (subgrid) {
        formStyle.display = 'grid';
        formStyle.gridTemplateRows = 'subgrid'
        formStyle.gridRow = '1/17'
      }

      content = <form onSubmit={this.onSubmit} style={formStyle}>
        <input type='hidden' name='GameId' value={gameInfo.gameId ?? game_id}/>
        {isRequest && <input type='hidden' name='GameRequest' value={game_id.toString().replace('request-', '')}/>}
        {requestId && <input type="hidden" name="GameRequest" value={requestId}/>}
        <h2 className={commonCss['h3']} style="margin-bottom: 40px;">{formTitle}</h2>
        {[GameEditorMode.edit, GameEditorMode.suggest].includes(mode) && suggestions?.notes &&
          <div className={formCss['form-group']}>
            <h3 className={commonCss['h3']}>Замечания</h3>
            {this.renderSuggestions('notes')}
          </div>}
        <h3 className={commonCss['h3']}>Название игры</h3>
        <div className={formCss['form-group']}>
          {[GameEditorMode.suggest, GameEditorMode.suggest_game].includes(mode) && <>
            <input
              className={cn({
                [formCss['input']]: true,
                [formCss['input--error']]: gameNameError,
              })}
              id='GameName'
              name='GameName'
              value={mainTitle ?? ''}
              onInput={this.setTitle}
              style='margin-top: 24px'
            />
            {gameNameError && <div className={formCss['error-hint']}>{gameNameError}</div>}
          </>}
          {mode === GameEditorMode.edit &&
            <GameTitlesEditor
              titles={gameInfo?.titles ?? []}
              updateTitles={this.updateTitles}
              checkName={this.checkGameName}
              checkUrl={this.checkGameUrl}
              addError={this.addError}
              removeError={this.removeError}
            />
          }
          {this.renderSuggestions('GameName', 'titles')}
        </div>
        <div className={formCss['form-group']}>
          <label className={formCss['label']} htmlFor='steam_id'>Страница в Steam</label>
          <input
            className={formCss['input']}
            id='steam_id'
            type='url'
            name='steam_id'
            defaultValue={gameInfo?.steam_id ?? ''}
            pattern='https:\/\/store.steampowered.com\/app\/\d+\/?.*'
            onChange={this.setSteamIdFromLink}
          />
          {this.renderSuggestions('steam_id')}
        </div>
        <h3 className={commonCss['h3']} style='margin-bottom: 24px'>Обложки</h3>
        <div className={formCss['form-group']}>
          <GameCoversEditor
            covers={gameInfo?.covers ?? {
              square_poster: '',
              large_poster: '',
            }}
            steamId={gameInfo?.SteamId}
            gameName={mainTitle}
            oldLogo={gameInfo?.OldLogo}
          />
          {this.renderSuggestions('square_poster')}
          {this.renderSuggestions('large_poster')}
        </div>
        <h3 className={commonCss['h3']} style='margin-bottom: 24px'>Информация</h3>
        <div className={formCss['form-group']}>
          <label className={formCss['label']}>Дата выхода</label>
          <GameDateEditor date={gameInfo?.GameDate ?? '0000-00-00'} name='GameDate'/>
          {this.renderSuggestions('GameDate')}
        </div>
        <div className={formCss['form-group']}>
          <label className={formCss['label']} style='margin-bottom: 8px'>Платформы</label>
          <GamePlatformsEditor platforms={gameInfo?.GamePlatform ?? []} name='GamePlatform'/>
          {this.renderSuggestions('GamePlatform')}
        </div>
        <div className={formCss['form-group']}>
          <label className={formCss['label']}>Теги</label>
          <GameTagsEditor
            tags={gameInfo?.GameJanr ?? []}
            name='GameJanr'
            showHidden={true}
          />
          {this.renderSuggestions('GameJanr')}
        </div>
        <div className={formCss['form-group']}>
          <label
            htmlFor=''
            className={formCss['label']}
            style='margin-bottom: 8px; display: flex; align-items: center; column-gap: 8px;'
          >Разработчики {addCompany}</label>
          <GameCompaniesEditor companies={gameInfo?.GameDevelopers ?? []} name='GameCompany' type='dev'/>
          {this.renderSuggestions('GameCompany', 'GameDevelopers')}
        </div>
        <div className={formCss['form-group']}>
          <label
            htmlFor=''
            className={formCss['label']}
            style='margin-bottom: 8px; display: flex; align-items: center; column-gap: 8px;'
          >Издатели {addCompany}</label>
          <GameCompaniesEditor companies={gameInfo?.GamePublishers ?? []} name='GameCompany' type='izd'/>
          {this.renderSuggestions('GameCompany', 'GamePublishers')}
        </div>
        <div className={formCss['form-group']}>
          <label className={formCss['label']} htmlFor='GameSite'>Официальный сайт</label>
          <input className={formCss['input']} id='GameSite' name='GameSite' value={gameInfo?.GameSite ?? ''}/>
          {this.renderSuggestions('GameSite')}
        </div>
        <div className={formCss['form-group']}>
          <label className={formCss['label']} htmlFor='GameRusSite'>Русскоязычный сайт</label>
          <input className={formCss['input']} id='GameRusSite' name='GameRusSite' value={gameInfo?.GameRusSite ?? ''}/>
          {this.renderSuggestions('GameRusSite')}
        </div>
        {mode === GameEditorMode.edit && <>
          <div className={formCss['form-group']}>
            <label className={formCss['label']}>Краткое описание</label>
            <textarea className={formCss['input']} name='GameInfo' value={gameInfo?.GameInfo ?? ''}/>
          </div>
        </>}
        {[GameEditorMode.suggest, GameEditorMode.suggest_game].includes(mode) && <div className={formCss['form-group']}>
          <h3 className={commonCss['h3']}>Замечания</h3>
          <p className={formCss['hint']} style='margin-bottom: 16px'>Это поле на крайний случай, если нужно поправить
            что-то, чего нет в полях выше</p>
          <textarea className={formCss['input']} name='notes' value={gameInfo.notes ?? ''}/>
        </div>}
        <button type='submit' className={buttonsCss['primary']} disabled={!isValid}>
          {GameEditorMode.suggest ? 'Отправить' : 'Сохранить'}
        </button>
      </form>
    }

    return content
  }
}
